import { Analysis } from '../../../types/Survey/Analysis/Analysis'
import { AnwesenheitPercentage, BueroType, DesksharingPercentage, ErgebnisByBueroType, Values } from '../../../types/Survey/Analysis/Calculation'
import {
  PrebuildEinzelarbeitSubType,
  PrebuildMeetingGroesseBesprechungSubType,
  PrebuildMeetingGroesseCoworkingSubType,
  PrebuildType,
} from '../../../types/Survey/Element'
import { Survey, SurveyContext } from '../../../types/Survey/Survey'
import flex from './byBueroType/flex'
import komfort from './byBueroType/komfort'
import agil from './byBueroType/agil'
import zellenstruktur from './byBueroType/zellenstruktur'
import openSpace from './byBueroType/openSpace'
import getSliderValue from './getSliderValue'
import { getPercentage } from '../../../utility'
import { BueroTypeResult } from '../../../types/Survey/Analysis/FactorValues'
import { Concept, ModuleSizes } from '../../../types/Survey/Analysis/Concept'
import getTotal from './byBueroType/helper/getTotal'
import { ProductionDistance } from '../../../types/OrganizationUnit'

export const calculateResultsOfUnit = (bueroType: BueroType, values: Values, concept?: Concept): BueroTypeResult => {
  if (bueroType === BueroType.KOMFORT) {
    return komfort(values, concept)
  } else if (bueroType === BueroType.FLEX) {
    return flex(values, concept)
  } else if (bueroType === BueroType.AGIL) {
    return agil(values, concept)
  } else if (bueroType === BueroType.ZELLSTRUKTUR) {
    return zellenstruktur(values, concept)
  } else if (bueroType === BueroType.OPEN_SPACE) {
    return openSpace(values, concept)
  }
}

export const calculateAnwesenheitPercentage = (survey: Survey, analysis: Analysis, bueroType: BueroType, values: Values): AnwesenheitPercentage => {
  const calculatedAnwesenheitPercentage = getSliderValue(survey, analysis, PrebuildType.ANWESENHEIT)

  const isPreset = !!values?.anwesenheitPercentage?.percentage

  const standard: AnwesenheitPercentage = {
    percentage: 100,
    originalPercentage: isPreset ? values.anwesenheitPercentage.originalPercentage : 100,
    answerAmount: 1,
  }

  if (bueroType === BueroType.KOMFORT) {
    return standard
  } else if (bueroType === BueroType.FLEX) {
    return {
      percentage: isPreset ? values.anwesenheitPercentage.percentage : calculatedAnwesenheitPercentage.average,
      originalPercentage: isPreset ? values.anwesenheitPercentage.originalPercentage : calculatedAnwesenheitPercentage.average,
      answerAmount: calculatedAnwesenheitPercentage.answers.length,
    }
  } else if (bueroType === BueroType.AGIL) {
    return {
      percentage: isPreset ? values.anwesenheitPercentage.percentage : calculatedAnwesenheitPercentage.average,
      originalPercentage: isPreset ? values.anwesenheitPercentage.originalPercentage : calculatedAnwesenheitPercentage.average,
      answerAmount: calculatedAnwesenheitPercentage.answers.length,
    }
  } else if (bueroType === BueroType.ZELLSTRUKTUR) {
    return standard
  } else if (bueroType === BueroType.OPEN_SPACE) {
    return standard
  }

  return standard
}

export const calculateDesksharingPercentage = (survey: Survey, analysis: Analysis, bueroType: BueroType, values: Values): DesksharingPercentage => {
  const anwesenheitPercentage = values.anwesenheitPercentage.percentage

  const isPreset = !!values?.desksharingPercentage?.percentage

  const defaultPercentage: DesksharingPercentage = {
    percentage: 100,
    originalPercentage: isPreset ? values.desksharingPercentage.originalPercentage : 100,
  }

  if (bueroType === BueroType.KOMFORT) {
    return defaultPercentage
  } else if (bueroType === BueroType.FLEX) {
    return {
      percentage: anwesenheitPercentage,
      originalPercentage: isPreset ? values.desksharingPercentage.originalPercentage : anwesenheitPercentage,
    }
  } else if (bueroType === BueroType.AGIL) {
    const einzelarbeit = values.factor.arbeitstagPercentage.EINZELARBEIT
    const kommunikation = values.factor.arbeitstagPercentage.KOMMUNIKATION.value / 100
    const kollaboration = values.factor.arbeitstagPercentage.KOLLABORATION.value / 100
    const percentage =
      getPercentage(values.anwesenheitPercentage.percentage, einzelarbeit.value) +
      values.anwesenheitPercentage.percentage * (kommunikation / 2 + kollaboration / 2)
    return {
      percentage,
      originalPercentage: isPreset ? values.desksharingPercentage.originalPercentage : percentage,
    }
  } else if (bueroType === BueroType.ZELLSTRUKTUR) {
    return defaultPercentage
  } else if (bueroType === BueroType.OPEN_SPACE) {
    return defaultPercentage
  }

  return defaultPercentage
}

export const getErgebnisByValues = (presetResult: BueroTypeResult, unitValues: Values): BueroTypeResult => {
  if (unitValues) {
    const hasPreviousValues = !!presetResult?.[PrebuildType.EINZELARBEIT]
    const result: BueroTypeResult = {
      [PrebuildType.EINZELARBEIT]: {
        [PrebuildEinzelarbeitSubType.KLASSISCHE_ARBEIT]: {
          theoreticalQM:
            (hasPreviousValues
              ? presetResult[PrebuildType[PrebuildType.EINZELARBEIT]][PrebuildEinzelarbeitSubType.KLASSISCHE_ARBEIT].theoreticalQM
              : 0) + unitValues.ergebnisse[PrebuildType.EINZELARBEIT][PrebuildEinzelarbeitSubType.KLASSISCHE_ARBEIT].theoreticalQM,
          theroeticalSpaceAmount:
            (hasPreviousValues ? presetResult[PrebuildType.EINZELARBEIT][PrebuildEinzelarbeitSubType.KLASSISCHE_ARBEIT].theroeticalSpaceAmount : 0) +
            unitValues.ergebnisse[PrebuildType.EINZELARBEIT][PrebuildEinzelarbeitSubType.KLASSISCHE_ARBEIT].theroeticalSpaceAmount,
          practicalSpaceAmount:
            (hasPreviousValues ? presetResult[PrebuildType.EINZELARBEIT][PrebuildEinzelarbeitSubType.KLASSISCHE_ARBEIT].practicalSpaceAmount : 0) +
            unitValues.ergebnisse[PrebuildType.EINZELARBEIT][PrebuildEinzelarbeitSubType.KLASSISCHE_ARBEIT].practicalSpaceAmount,
          practicalQM:
            (hasPreviousValues ? presetResult[PrebuildType.EINZELARBEIT][PrebuildEinzelarbeitSubType.KLASSISCHE_ARBEIT].practicalQM : 0) +
            unitValues.ergebnisse[PrebuildType.EINZELARBEIT][PrebuildEinzelarbeitSubType.KLASSISCHE_ARBEIT].practicalQM,
          arbeitsplatzAmount:
            (hasPreviousValues ? presetResult[PrebuildType.EINZELARBEIT][PrebuildEinzelarbeitSubType.KLASSISCHE_ARBEIT].arbeitsplatzAmount : 0) +
            unitValues.ergebnisse[PrebuildType.EINZELARBEIT][PrebuildEinzelarbeitSubType.KLASSISCHE_ARBEIT].arbeitsplatzAmount,
        },
        [PrebuildEinzelarbeitSubType.FOKUSSIERTE_ARBEIT_MORE_THEN_HOUR]: {
          theoreticalQM:
            (hasPreviousValues
              ? presetResult[PrebuildType.EINZELARBEIT][PrebuildEinzelarbeitSubType.FOKUSSIERTE_ARBEIT_MORE_THEN_HOUR].theoreticalQM
              : 0) + unitValues.ergebnisse[PrebuildType.EINZELARBEIT][PrebuildEinzelarbeitSubType.FOKUSSIERTE_ARBEIT_MORE_THEN_HOUR].theoreticalQM,
          theroeticalSpaceAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.EINZELARBEIT][PrebuildEinzelarbeitSubType.FOKUSSIERTE_ARBEIT_MORE_THEN_HOUR].theroeticalSpaceAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.EINZELARBEIT][PrebuildEinzelarbeitSubType.FOKUSSIERTE_ARBEIT_MORE_THEN_HOUR].theroeticalSpaceAmount,
          practicalSpaceAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.EINZELARBEIT][PrebuildEinzelarbeitSubType.FOKUSSIERTE_ARBEIT_MORE_THEN_HOUR].practicalSpaceAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.EINZELARBEIT][PrebuildEinzelarbeitSubType.FOKUSSIERTE_ARBEIT_MORE_THEN_HOUR].practicalSpaceAmount,
          practicalQM:
            (hasPreviousValues
              ? presetResult[PrebuildType.EINZELARBEIT][PrebuildEinzelarbeitSubType.FOKUSSIERTE_ARBEIT_MORE_THEN_HOUR].practicalQM
              : 0) + unitValues.ergebnisse[PrebuildType.EINZELARBEIT][PrebuildEinzelarbeitSubType.FOKUSSIERTE_ARBEIT_MORE_THEN_HOUR].practicalQM,
          arbeitsplatzAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.EINZELARBEIT][PrebuildEinzelarbeitSubType.FOKUSSIERTE_ARBEIT_MORE_THEN_HOUR].arbeitsplatzAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.EINZELARBEIT][PrebuildEinzelarbeitSubType.FOKUSSIERTE_ARBEIT_MORE_THEN_HOUR].arbeitsplatzAmount,
        },
        [PrebuildEinzelarbeitSubType.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR]: {
          theoreticalQM:
            (hasPreviousValues
              ? presetResult[PrebuildType.EINZELARBEIT][PrebuildEinzelarbeitSubType.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR].theoreticalQM
              : 0) + unitValues.ergebnisse[PrebuildType.EINZELARBEIT][PrebuildEinzelarbeitSubType.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR].theoreticalQM,
          theroeticalSpaceAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.EINZELARBEIT][PrebuildEinzelarbeitSubType.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR].theroeticalSpaceAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.EINZELARBEIT][PrebuildEinzelarbeitSubType.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR].theroeticalSpaceAmount,
          practicalSpaceAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.EINZELARBEIT][PrebuildEinzelarbeitSubType.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR].practicalSpaceAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.EINZELARBEIT][PrebuildEinzelarbeitSubType.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR].practicalSpaceAmount,
          practicalQM:
            (hasPreviousValues
              ? presetResult[PrebuildType.EINZELARBEIT][PrebuildEinzelarbeitSubType.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR].practicalQM
              : 0) + unitValues.ergebnisse[PrebuildType.EINZELARBEIT][PrebuildEinzelarbeitSubType.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR].practicalQM,
          arbeitsplatzAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.EINZELARBEIT][PrebuildEinzelarbeitSubType.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR].arbeitsplatzAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.EINZELARBEIT][PrebuildEinzelarbeitSubType.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR].arbeitsplatzAmount,
        },
      },
      [PrebuildType.MEETING_GROESSE_BESPRECHUNG]: {
        [PrebuildMeetingGroesseBesprechungSubType.PERSON_2]: {
          theoreticalQM:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_2].theoreticalQM
              : 0) + unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_2].theoreticalQM,
          theroeticalSpaceAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_2].theroeticalSpaceAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_2].theroeticalSpaceAmount,
          practicalSpaceAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_2].practicalSpaceAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_2].practicalSpaceAmount,
          practicalQM:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_2].practicalQM
              : 0) + unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_2].practicalQM,
          arbeitsplatzAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_2].arbeitsplatzAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_2].arbeitsplatzAmount,
        },
        [PrebuildMeetingGroesseBesprechungSubType.PERSON_3_4]: {
          theoreticalQM:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_3_4].theoreticalQM
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_3_4].theoreticalQM,
          theroeticalSpaceAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_3_4].theroeticalSpaceAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_3_4]
              .theroeticalSpaceAmount,
          practicalSpaceAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_3_4].practicalSpaceAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_3_4].practicalSpaceAmount,
          practicalQM:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_3_4].practicalQM
              : 0) + unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_3_4].practicalQM,
          arbeitsplatzAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_3_4].arbeitsplatzAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_3_4].arbeitsplatzAmount,
        },
        [PrebuildMeetingGroesseBesprechungSubType.PERSON_5_8]: {
          theoreticalQM:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_5_8].theoreticalQM
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_5_8].theoreticalQM,
          theroeticalSpaceAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_5_8].theroeticalSpaceAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_5_8]
              .theroeticalSpaceAmount,
          practicalSpaceAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_5_8].practicalSpaceAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_5_8].practicalSpaceAmount,
          practicalQM:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_5_8].practicalQM
              : 0) + unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_5_8].practicalQM,
          arbeitsplatzAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_5_8].arbeitsplatzAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_5_8].arbeitsplatzAmount,
        },
        [PrebuildMeetingGroesseBesprechungSubType.PERSON_9_12]: {
          theoreticalQM:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_9_12].theoreticalQM
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_9_12].theoreticalQM,
          theroeticalSpaceAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_9_12].theroeticalSpaceAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_9_12]
              .theroeticalSpaceAmount,
          practicalSpaceAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_9_12].practicalSpaceAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_9_12]
              .practicalSpaceAmount,
          practicalQM:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_9_12].practicalQM
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_9_12].practicalQM,
          arbeitsplatzAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_9_12].arbeitsplatzAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_9_12].arbeitsplatzAmount,
        },
        [PrebuildMeetingGroesseBesprechungSubType.PERSON_13_16]: {
          theoreticalQM:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_13_16].theoreticalQM
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_13_16].theoreticalQM,
          theroeticalSpaceAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_13_16].theroeticalSpaceAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_13_16]
              .theroeticalSpaceAmount,
          practicalSpaceAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_13_16].practicalSpaceAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_13_16]
              .practicalSpaceAmount,
          practicalQM:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_13_16].practicalQM
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_13_16].practicalQM,
          arbeitsplatzAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_13_16].arbeitsplatzAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_BESPRECHUNG][PrebuildMeetingGroesseBesprechungSubType.PERSON_13_16].arbeitsplatzAmount,
        },
      },
      [PrebuildType.MEETING_GROESSE_COWORKING]: {
        [PrebuildMeetingGroesseCoworkingSubType.PERSON_2_4]: {
          theoreticalQM:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_2_4].theoreticalQM
              : 0) + unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_2_4].theoreticalQM,
          theroeticalSpaceAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_2_4].theroeticalSpaceAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_2_4].theroeticalSpaceAmount,
          practicalSpaceAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_2_4].practicalSpaceAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_2_4].practicalSpaceAmount,
          practicalQM:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_2_4].practicalQM
              : 0) + unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_2_4].practicalQM,
          arbeitsplatzAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_2_4].arbeitsplatzAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_2_4].arbeitsplatzAmount,
        },
        [PrebuildMeetingGroesseCoworkingSubType.PERSON_5_8]: {
          theoreticalQM:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_5_8].theoreticalQM
              : 0) + unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_5_8].theoreticalQM,
          theroeticalSpaceAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_5_8].theroeticalSpaceAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_5_8].theroeticalSpaceAmount,
          practicalSpaceAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_5_8].practicalSpaceAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_5_8].practicalSpaceAmount,
          practicalQM:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_5_8].practicalQM
              : 0) + unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_5_8].practicalQM,
          arbeitsplatzAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_5_8].arbeitsplatzAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_5_8].arbeitsplatzAmount,
        },
        [PrebuildMeetingGroesseCoworkingSubType.PERSON_9_12]: {
          theoreticalQM:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_9_12].theoreticalQM
              : 0) + unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_9_12].theoreticalQM,
          theroeticalSpaceAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_9_12].theroeticalSpaceAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_9_12].theroeticalSpaceAmount,
          practicalSpaceAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_9_12].practicalSpaceAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_9_12].practicalSpaceAmount,
          practicalQM:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_9_12].practicalQM
              : 0) + unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_9_12].practicalQM,
          arbeitsplatzAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_9_12].arbeitsplatzAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_9_12].arbeitsplatzAmount,
        },
        [PrebuildMeetingGroesseCoworkingSubType.PERSON_13_16]: {
          theoreticalQM:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_13_16].theoreticalQM
              : 0) + unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_13_16].theoreticalQM,
          theroeticalSpaceAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_13_16].theroeticalSpaceAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_13_16].theroeticalSpaceAmount,
          practicalSpaceAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_13_16].practicalSpaceAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_13_16].practicalSpaceAmount,
          practicalQM:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_13_16].practicalQM
              : 0) + unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_13_16].practicalQM,
          arbeitsplatzAmount:
            (hasPreviousValues
              ? presetResult[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_13_16].arbeitsplatzAmount
              : 0) +
            unitValues.ergebnisse[PrebuildType.MEETING_GROESSE_COWORKING][PrebuildMeetingGroesseCoworkingSubType.PERSON_13_16].arbeitsplatzAmount,
        },
      },
      HYBRID: {
        [ModuleSizes.XS]: {
          practicalQM:
            (hasPreviousValues ? presetResult.HYBRID[ModuleSizes.XS].practicalQM : 0) + unitValues.ergebnisse.HYBRID[ModuleSizes.XS].practicalQM,
          practicalSpaceAmount:
            (hasPreviousValues ? presetResult.HYBRID[ModuleSizes.XS].practicalSpaceAmount : 0) +
            unitValues.ergebnisse.HYBRID[ModuleSizes.XS].practicalSpaceAmount,
          arbeitsplatzAmount:
            (hasPreviousValues ? presetResult.HYBRID[ModuleSizes.XS].arbeitsplatzAmount : 0) +
            unitValues.ergebnisse.HYBRID[ModuleSizes.XS].arbeitsplatzAmount,
        },
        [ModuleSizes.S]: {
          practicalQM:
            (hasPreviousValues ? presetResult.HYBRID[ModuleSizes.S].practicalQM : 0) + unitValues.ergebnisse.HYBRID[ModuleSizes.S].practicalQM,
          practicalSpaceAmount:
            (hasPreviousValues ? presetResult.HYBRID[ModuleSizes.S].practicalSpaceAmount : 0) +
            unitValues.ergebnisse.HYBRID[ModuleSizes.S].practicalSpaceAmount,
          arbeitsplatzAmount:
            (hasPreviousValues ? presetResult.HYBRID[ModuleSizes.S].arbeitsplatzAmount : 0) +
            unitValues.ergebnisse.HYBRID[ModuleSizes.S].arbeitsplatzAmount,
        },
        [ModuleSizes.M]: {
          practicalQM:
            (hasPreviousValues ? presetResult.HYBRID[ModuleSizes.M].practicalQM : 0) + unitValues.ergebnisse.HYBRID[ModuleSizes.M].practicalQM,
          practicalSpaceAmount:
            (hasPreviousValues ? presetResult.HYBRID[ModuleSizes.M].practicalSpaceAmount : 0) +
            unitValues.ergebnisse.HYBRID[ModuleSizes.M].practicalSpaceAmount,
          arbeitsplatzAmount:
            (hasPreviousValues ? presetResult.HYBRID[ModuleSizes.M].arbeitsplatzAmount : 0) +
            unitValues.ergebnisse.HYBRID[ModuleSizes.M].arbeitsplatzAmount,
        },
        [ModuleSizes.L]: {
          practicalQM:
            (hasPreviousValues ? presetResult.HYBRID[ModuleSizes.M].practicalQM : 0) + unitValues.ergebnisse.HYBRID[ModuleSizes.M].practicalQM,
          practicalSpaceAmount:
            (hasPreviousValues ? presetResult.HYBRID[ModuleSizes.M].practicalSpaceAmount : 0) +
            unitValues.ergebnisse.HYBRID[ModuleSizes.M].practicalSpaceAmount,
          arbeitsplatzAmount:
            (hasPreviousValues ? presetResult.HYBRID[ModuleSizes.M].arbeitsplatzAmount : 0) +
            unitValues.ergebnisse.HYBRID[ModuleSizes.M].arbeitsplatzAmount,
        },
        [ModuleSizes.XL]: {
          practicalQM:
            (hasPreviousValues ? presetResult.HYBRID[ModuleSizes.XL].practicalQM : 0) + unitValues.ergebnisse.HYBRID[ModuleSizes.XL].practicalQM,
          practicalSpaceAmount:
            (hasPreviousValues ? presetResult.HYBRID[ModuleSizes.XL].practicalSpaceAmount : 0) +
            unitValues.ergebnisse.HYBRID[ModuleSizes.XL].practicalSpaceAmount,
          arbeitsplatzAmount:
            (hasPreviousValues ? presetResult.HYBRID[ModuleSizes.XL].arbeitsplatzAmount : 0) +
            unitValues.ergebnisse.HYBRID[ModuleSizes.XL].arbeitsplatzAmount,
        },
      },
      [PrebuildType.STAUFLAECHE]: {
        practicalQM:
          (hasPreviousValues ? presetResult[PrebuildType.STAUFLAECHE].practicalQM : 0) + unitValues.ergebnisse[PrebuildType.STAUFLAECHE].practicalQM,
        originalPracticalQM:
          (hasPreviousValues ? presetResult[PrebuildType.STAUFLAECHE].practicalQM : 0) + unitValues.ergebnisse[PrebuildType.STAUFLAECHE].practicalQM,
      },
      [PrebuildType.SONDERBEDARFE]: hasPreviousValues
        ? presetResult[PrebuildType.SONDERBEDARFE].concat(unitValues.ergebnisse[PrebuildType.SONDERBEDARFE])
        : unitValues.ergebnisse[PrebuildType.SONDERBEDARFE],
      [PrebuildType.EINZELBUEROS]: {
        amount: (hasPreviousValues ? presetResult[PrebuildType.EINZELBUEROS].amount : 0) + unitValues.ergebnisse[PrebuildType.EINZELBUEROS].amount,
        originalAmount:
          (hasPreviousValues ? presetResult[PrebuildType.EINZELBUEROS].amount : 0) + unitValues.ergebnisse[PrebuildType.EINZELBUEROS].amount,
        practicalQM:
          (hasPreviousValues ? presetResult[PrebuildType.EINZELBUEROS].practicalQM : 0) +
          unitValues.ergebnisse[PrebuildType.EINZELBUEROS].practicalQM,
      },
      [PrebuildType.WORKSTATIONS]: {
        amount: (hasPreviousValues ? presetResult[PrebuildType.WORKSTATIONS].amount : 0) + unitValues.ergebnisse[PrebuildType.WORKSTATIONS].amount,
        originalAmount:
          (hasPreviousValues ? presetResult[PrebuildType.WORKSTATIONS].amount : 0) + unitValues.ergebnisse[PrebuildType.WORKSTATIONS].amount,
      },
      drucker: {
        practicalQM:
          (hasPreviousValues ? presetResult.drucker.practicalQM : 0) +
          (!isNaN(unitValues.ergebnisse.drucker.practicalQM) ? unitValues.ergebnisse.drucker.practicalQM : 0),
        originalPracticalQM:
          (hasPreviousValues ? presetResult.drucker.practicalQM : 0) +
          (!isNaN(unitValues.ergebnisse.drucker.practicalQM) ? unitValues.ergebnisse.drucker.practicalQM : 0),
      },
      locker: {
        practicalQM:
          (hasPreviousValues ? presetResult.locker.practicalQM : 0) +
          (!isNaN(unitValues.ergebnisse.locker.practicalQM) ? unitValues.ergebnisse.locker.practicalQM : 0),
        originalPracticalQM:
          (hasPreviousValues ? presetResult.locker.practicalQM : 0) +
          (!isNaN(unitValues.ergebnisse.locker.practicalQM) ? unitValues.ergebnisse.locker.practicalQM : 0),
      },
      workcafe: {
        practicalQM:
          (hasPreviousValues ? presetResult.workcafe.practicalQM : 0) +
          (!isNaN(unitValues.ergebnisse.workcafe.practicalQM) ? unitValues.ergebnisse.workcafe.practicalQM : 0),
        originalPracticalQM:
          (hasPreviousValues ? presetResult.workcafe.practicalQM : 0) +
          (!isNaN(unitValues.ergebnisse.workcafe.practicalQM) ? unitValues.ergebnisse.workcafe.practicalQM : 0),
      },
      garderobe: {
        practicalQM:
          (hasPreviousValues ? presetResult.garderobe.practicalQM : 0) +
          (!isNaN(unitValues.ergebnisse.garderobe.practicalQM) ? unitValues.ergebnisse.garderobe.practicalQM : 0),
        originalPracticalQM:
          (hasPreviousValues ? presetResult.garderobe.practicalQM : 0) +
          (!isNaN(unitValues.ergebnisse.garderobe.practicalQM) ? unitValues.ergebnisse.garderobe.practicalQM : 0),
      },
      schreibtische: {
        theoreticalAmount:
          (hasPreviousValues ? presetResult.schreibtische.theoreticalAmount : 0) + unitValues.ergebnisse.schreibtische.theoreticalAmount,
        practicalAmount: (hasPreviousValues ? presetResult.schreibtische.practicalAmount : 0) + unitValues.ergebnisse.schreibtische.practicalAmount,
      },
      verkehrsflaechen: {
        practicalQM: (hasPreviousValues ? presetResult.verkehrsflaechen.practicalQM : 0) + unitValues.ergebnisse.verkehrsflaechen.practicalQM,
      },
      mitarbeiter: {
        amount: (hasPreviousValues ? presetResult.mitarbeiter.amount : 0) + unitValues.ergebnisse.mitarbeiter.amount,
      },
      desksharingPercentages: {
        percentages: hasPreviousValues
          ? [...presetResult.desksharingPercentages.percentages, unitValues.desksharingPercentage.percentage]
          : [unitValues.desksharingPercentage.percentage],
      },
      anwesenheitPercentages: {
        percentages: hasPreviousValues
          ? [...presetResult.anwesenheitPercentages.percentages, unitValues.anwesenheitPercentage.percentage]
          : [unitValues.anwesenheitPercentage.percentage],
      },
      anwesenheit: {
        averages: hasPreviousValues
          ? [...presetResult.anwesenheit.averages, unitValues.anwesenheitPercentage.percentage]
          : [unitValues.anwesenheitPercentage.percentage],
        answerAmounts: hasPreviousValues
          ? [...presetResult.anwesenheit.answerAmounts, unitValues.anwesenheitPercentage.answerAmount]
          : [unitValues.anwesenheitPercentage.answerAmount],
      },
    }
    return result
  }
}

export const getSumOfConcept = (context: SurveyContext, concept: Concept, ergebnisByBueroType: ErgebnisByBueroType): BueroTypeResult => {
  // create the overall sum object
  const sumObject = Object.keys(ergebnisByBueroType)
    .filter((k) => k !== 'sum')
    .reduce((acc, unitUUID) => {
      const values = ergebnisByBueroType[unitUUID]
      const result = getErgebnisByValues(acc, values)
      // result.total = getTotal(result)
      return result
    }, {} as BueroTypeResult)

  // create the production distance sum objects
  sumObject.productionDistance = {
    [ProductionDistance.FERN]: null,
    [ProductionDistance.NAH]: null,
  }

  sumObject.floor = context.floors.reduce((acc, floor) => {
    acc[floor.uuid] = null
    return acc
  }, {})

  for (const productionDistance of Object.values(ProductionDistance)) {
    const productionDistanceResult = Object.keys(ergebnisByBueroType)
      .filter((k) => k !== 'sum')
      .filter((k) => concept.units[k].productionDistance === productionDistance)
      .reduce((acc, unitUUID) => {
        const values = ergebnisByBueroType[unitUUID]
        const result = getErgebnisByValues(acc, values)
        // result.total = getTotal(result)
        return result
      }, {} as BueroTypeResult)
    productionDistanceResult.total = getTotal(productionDistanceResult)
    sumObject.productionDistance[productionDistance] = productionDistanceResult
  }

  for (const floor of context.floors) {
    const validFloorUUIDs = concept.floorMapping.filter((f) => f.floorUUID === floor.uuid).map((f) => f.targetUUID)
    const floorResult = Object.keys(ergebnisByBueroType)
      .filter((k) => k !== 'sum')
      .filter((k) => validFloorUUIDs.includes(k))
      .reduce((acc, unitUUID) => {
        const values = ergebnisByBueroType[unitUUID]
        const result = getErgebnisByValues(acc, values)
        // result.total = getTotal(result)
        return result
      }, {} as BueroTypeResult)
    floorResult.UEBERGEORDNETE_BEDARFE = concept.optimization.UEBERGEORDNETE_BEDARFE
    floorResult.total = getTotal(floorResult, true)
    sumObject.floor[floor.uuid] = floorResult
  }

  // by adding true to getTotal, the UEBERGEORDNETEN_BEDARFE are added
  sumObject.UEBERGEORDNETE_BEDARFE = concept.optimization.UEBERGEORDNETE_BEDARFE
  sumObject.total = getTotal(sumObject, true)
  return sumObject
}
